import React, { useEffect, useMemo, useState } from "react";
import { Pagination,Select } from "antd";
import "react-quill/dist/quill.snow.css";
import {
  Newspaper,
  X as XIcon,
} from "lucide-react";
import { useQuery } from "react-query";
import useAxiosInstance from "../../lib/useAxiosInstance";
import { NewsReportCard } from "../ElectionMode/RightPanel";
import { NewsDetailModal } from "../ElectionMode/RightPanel";

// ─── Full-screen News page ────────────────────────────────────────────────────
// Opened from the "Media Intelligence" card. Shows the same news list with the
// same filters, but as a full page with classic (page-number) pagination and a
// detail modal on row click.
export const NewsFullPage = ({ electionModeId, filters, t, onClose }) => {
  const tr = (key, fallback) => (t ? t(`ElectionMode.${key}`) : fallback);
  const axiosInstance = useAxiosInstance();

  // Location filters live on the page itself (seeded from the incoming filters).
  const [region, setRegion] = useState(filters?.region || "");
  const [district, setDistrict] = useState(filters?.district || "");
  const [constituency, setConstituency] = useState(filters?.constituency || "");
  const party = filters?.party || "BJP";

  const [filterCategory, setFilterCategory] = useState("");
  const [dateRange, setDateRange] = useState(""); // "" | today | last7 | lastMonth | custom
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [selectedNews, setSelectedNews] = useState(null);

  const isCustom = dateRange === "custom";
  const effectiveStartDate = isCustom ? startDate : "";
  const effectiveEndDate = isCustom ? endDate : "";

  // ── Location filter options (regions / districts / constituencies) ──────────
  // Fetched from the dashboard endpoint, exactly like ElectionModeNavbar.
  const { data: filterData } = useQuery(
    ["media-intelligence-filter-options", electionModeId],
    async () => {
      const res = await axiosInstance.get(
        `/api/electionMode/dashboard/${electionModeId}`,
        { params: { party } }
      );
      return res.data?.data?.filters || {};
    },
    { enabled: Boolean(electionModeId), refetchOnWindowFocus: false, retry: 1, staleTime: 5 * 60 * 1000 }
  );

  const regionOptions = filterData?.regions || [];
  const allDistricts = filterData?.districts || [];
  const regionDistrictMap = filterData?.regionDistrictMap || {};
  const constituencyOptions = filterData?.constituencies || [];

  // When a region is selected, restrict districts to that region (BJP Kshetra).
  const districtOptions = useMemo(() => {
    const list = region && regionDistrictMap[region] ? regionDistrictMap[region] : allDistricts;
    return list;
  }, [region, allDistricts, regionDistrictMap]);

  const handleRegionChange = (value) => {
    setRegion(value || "");
    setDistrict("");
    setConstituency("");
  };
  const handleDistrictChange = (value) => {
    setDistrict(value || "");
    setConstituency("");
  };
  const handleConstituencyChange = (value) => {
    setConstituency(value || "");
  };

  // Reset to first page whenever a filter changes.
  useEffect(() => {
    setPage(1);
  }, [region, district, constituency, filterCategory, dateRange, effectiveStartDate, effectiveEndDate, pageSize]);

  const { data, isLoading, isError } = useQuery(
    [
      "election-mode-news-reports-page",
      electionModeId,
      region,
      district,
      constituency,
      filterCategory,
      dateRange,
      effectiveStartDate,
      effectiveEndDate,
      page,
      pageSize,
    ],
    async () => {
      const res = await axiosInstance.get(
        `api/electionMode/${electionModeId}/news-reports`,
        {
          params: {
            page,
            limit: pageSize,
            region: region || undefined,
            district: district || undefined,
            constituency: constituency || undefined,
            category: filterCategory || undefined,
            dateRange: dateRange || undefined,
            startDate: effectiveStartDate || undefined,
            endDate: effectiveEndDate || undefined,
          },
        }
      );
      return res.data?.data || {};
    },
    {
      enabled: Boolean(electionModeId),
      refetchOnWindowFocus: false,
      retry: 1,
      keepPreviousData: true,
    }
  );

  const rows = data?.news || [];
  const total = data?.total ?? 0;
  const categories = data?.categories || [];

  const hasAnyFilter =
    region || district || constituency || filterCategory || dateRange;

  const clearFilters = () => {
    setRegion("");
    setDistrict("");
    setConstituency("");
    setFilterCategory("");
    setDateRange("");
    setStartDate("");
    setEndDate("");
  };

  const selectClass = "min-w-[150px]";
  const popupProps = {
    getPopupContainer: () => document.body,
    dropdownStyle: { zIndex: 100050 },
  };

  return (
    <div className="flex flex-col h-[calc(100vh-1.5rem)] bg-slate-50 rounded-2xl overflow-hidden em-dark-modal m-2 shadow-lg border border-slate-200">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-[#0b1f3a] via-[#123865] to-[#0b65c2] flex-shrink-0">
        <button
          type="button"
          onClick={onClose}
          className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white transition-colors"
          title={tr("back", "Back")}
        >
          <XIcon size={18} />
        </button>
        <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
          <Newspaper size={20} className="text-orange-300" />
        </div>
        <div className="leading-tight">
          <div className="font-bold text-lg !text-white">
            {tr("mediaIntelligence", "Media Intelligence")}
          </div>
          <div className="text-[11px] text-blue-100/80">
            {total} {total === 1 ? tr("record", "record") : tr("records", "records")}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="px-6 py-4 bg-white border-b border-slate-200 flex-shrink-0">
        <div className="flex flex-wrap items-end justify-center gap-3 max-w-[1400px] mx-auto">
          <div className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold text-slate-500">{tr("region", "Region")}</span>
            <Select
              allowClear showSearch optionFilterProp="label"
              placeholder={tr("region", "Region")}
              value={region || undefined}
              onChange={handleRegionChange}
              options={regionOptions.map((r) => ({ value: r, label: r }))}
              className={selectClass}
              {...popupProps}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold text-slate-500">{tr("district", "District")}</span>
            <Select
              allowClear showSearch optionFilterProp="label"
              placeholder={tr("district", "District")}
              value={district || undefined}
              onChange={handleDistrictChange}
              options={districtOptions.map((d) => ({ value: d, label: d }))}
              className={selectClass}
              {...popupProps}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold text-slate-500">{tr("constituency", "Constituency")}</span>
            <Select
              allowClear showSearch optionFilterProp="label"
              placeholder={tr("constituency", "Constituency")}
              value={constituency || undefined}
              onChange={handleConstituencyChange}
              options={constituencyOptions.map((c) => ({ value: c, label: c }))}
              className={selectClass}
              {...popupProps}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold text-slate-500">{tr("category", "Category")}</span>
            <Select
              allowClear showSearch
              placeholder={tr("category", "Category")}
              value={filterCategory || undefined}
              onChange={(v) => setFilterCategory(v || "")}
              options={categories.map((c) => ({ label: c, value: c }))}
              className={selectClass}
              {...popupProps}
            />
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold text-slate-500">&nbsp;</span>
            <div className="flex items-center gap-1.5 flex-wrap">
              {[
                { key: "today", label: tr("today", "Today"), active: "bg-orange-400 text-white border-orange-400" },
                { key: "last7", label: tr("last7Days", "Last 7 Days"), active: "bg-blue-500 text-white border-blue-500" },
                { key: "lastMonth", label: tr("lastMonth", "Last Month"), active: "bg-green-500 text-white border-green-500" },
                { key: "custom", label: tr("dateRange", "Date Range"), active: "bg-purple-500 text-white border-purple-500" },
              ].map(({ key, label, active }) => (
                <button
                  key={key}
                  onClick={() => {
                    setDateRange(dateRange === key ? "" : key);
                    if (key !== "custom") {
                      setStartDate("");
                      setEndDate("");
                    }
                  }}
                  className={`text-xs px-3 py-2 rounded-full border transition-all ${
                    dateRange === key
                      ? active
                      : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {isCustom && (
            <div className="flex flex-col gap-1">
              <span className="text-[11px] font-semibold text-slate-500">&nbsp;</span>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="text-xs border border-slate-300 rounded-lg px-2 py-2 outline-none focus:border-blue-400"
                />
                <span className="text-slate-400 text-xs">–</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="text-xs border border-slate-300 rounded-lg px-2 py-2 outline-none focus:border-blue-400"
                />
              </div>
            </div>
          )}

          {hasAnyFilter && (
            <button
              onClick={clearFilters}
              className="text-xs font-semibold text-slate-500 hover:text-red-500 transition-colors px-3 py-2 rounded-lg hover:bg-red-50 self-end"
            >
              {tr("clear", "Clear")}
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        {isLoading ? (
          <div className="flex items-center justify-center py-24 text-slate-400 text-sm">
            {tr("loadingNews", "Loading news…")}
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-24 text-slate-400 gap-2">
            <div className="text-5xl">⚠️</div>
            <div className="text-sm font-medium">{tr("failedToLoadNews", "Failed to load news")}</div>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-slate-400 gap-2">
            <div className="text-5xl">📰</div>
            <div className="text-sm font-medium">{tr("noNewsAvailable", "No news available")}</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 max-w-[1500px] mx-auto">
            {rows.map((row) => (
              <NewsReportCard
                key={row._id}
                row={row}
                catList={categories}
                onOpen={setSelectedNews}
              />
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="px-6 py-3 bg-white border-t border-slate-200 flex justify-center flex-shrink-0">
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            showSizeChanger
            pageSizeOptions={["12", "24", "48", "96"]}
            onChange={(p, ps) => {
              setPage(p);
              setPageSize(ps);
            }}
          />
        </div>
      )}

      {/* Detail modal */}
      <NewsDetailModal
        row={selectedNews}
        open={Boolean(selectedNews)}
        onClose={() => setSelectedNews(null)}
        t={t}
      />
    </div>
  );
};